import { Lead } from '../../../domain/entities/Lead';
import { ILeadRepository } from '../../../domain/repositories/ILeadRepository';
import { sendWonConversions } from '../../../services/modules/leadTrackingService';
import { cancelFollowUpsForOpportunity } from '../../../services/modules/followUpService';
import { leadHistoryService } from '../../../services/leadHistoryService';
import { createModuleLogger } from '../../../utils/logger';

const log = createModuleLogger('[WinDealUseCase]');

export interface WinDealDTO {
    leadId: string;
    organizationId: string;
    finalValue: number; // Valor de fechamento (obrigatório e > 0)
}

export class WinDealUseCase {
    constructor(private leadRepository: ILeadRepository) { }

    async execute(dto: WinDealDTO): Promise<Lead> {
        // 1. Validar valor de fechamento
        if (dto.finalValue <= 0) {
            throw new Error('O valor do fechamento deve ser maior que zero');
        }

        // 2. Buscar negociação
        const lead = await this.leadRepository.findById(dto.leadId);

        if (!lead) {
            throw new Error('Negociação não encontrada');
        }

        // 2.1. Validar contato vinculado (necessário para rastreamento Meta Ads)
        if (!lead.contactId) {
            throw new Error('É necessário vincular um contato à negociação antes de marcá-la como ganha. O contato é obrigatório para o rastreamento de conversão.');
        }

        // 3. Validar se pode ser marcada como ganha
        if (!lead.isOpen()) {
            throw new Error(`Negociação já está ${lead.status === 'won' ? 'ganha' : 'perdida'}`);
        }

        // 4. Atualizar valor de fechamento
        lead.updateValue(dto.finalValue);

        // 5. Aplicar regra de negócio (domínio)
        lead.markAsWon();

        // 6. Persistir mudanças
        await this.leadRepository.save(lead, dto.organizationId);

        // 6.0. Cancela follow-ups agendados (negociação ganha não recebe mais mensagens automáticas)
        cancelFollowUpsForOpportunity(dto.leadId)
            .catch(err => log.warn('Falha ao cancelar follow-ups (won)', { name: err?.name }));

        // 6.1. Registrar no histórico
        leadHistoryService.addEvent({
            leadId: dto.leadId,
            organizationId: dto.organizationId,
            type: 'won',
            description: 'Negociação marcada como ganha',
            newValue: `R$ ${dto.finalValue.toFixed(2)}`
        }).catch(err => log.warn('Falha ao registrar histórico de ganho', { name: err?.name }));

        // Conversões: valor interno + Meta CAPI + Google (quando atribuído)
        sendWonConversions(dto.leadId, dto.finalValue, dto.organizationId, lead.contactId)
            .then((result) => {
                if (!result.meta.success) {
                    log.info('Purchase não enviado ao Meta', { reason: result.meta.error });
                }
                if (!result.google.skipped && !result.google.success) {
                    log.info('Purchase não enviado ao Google', { reason: result.google.error });
                }
            })
            .catch(err => log.warn('Erro ao enviar conversões de ganho', { name: err?.name }));

        return lead;
    }
}
